import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'

function BookingProcessFlow({ isOpen, onClose }) {
  const { t } = useTranslation();

  const steps = [
    { id: 1, title: t('booking_flow.steps.s1.title'), description: t('booking_flow.steps.s1.desc'), icon: 'fi fi-br-map' },
    { id: 2, title: t('booking_flow.steps.s2.title'), description: t('booking_flow.steps.s2.desc'), icon: 'fi fi-br-check-circle' },
    { id: 3, title: t('booking_flow.steps.s3.title'), description: t('booking_flow.steps.s3.desc'), icon: 'fi fi-br-users' },
    { id: 4, title: t('booking_flow.steps.s4.title'), description: t('booking_flow.steps.s4.desc'), icon: 'fi fi-br-computer' },
    { id: 5, title: t('booking_flow.steps.s5.title'), description: t('booking_flow.steps.s5.desc'), icon: 'fi fi-br-edit' },
    { id: 6, title: t('booking_flow.steps.s6.title'), description: t('booking_flow.steps.s6.desc'), icon: 'fi fi-br-plane-arrival' },
    { id: 7, title: t('booking_flow.steps.s7.title'), description: t('booking_flow.steps.s7.desc'), icon: 'fi fi-br-cocktail' },
    { id: 8, title: t('booking_flow.steps.s8.title'), description: t('booking_flow.steps.s8.desc'), icon: 'fi fi-br-camera' },
    { id: 9, title: t('booking_flow.steps.s9.title'), description: t('booking_flow.steps.s9.desc'), icon: 'fi fi-br-plane-departure' }
  ];

  const scrollContainerRef = useRef(null);

  useEffect(() => {
      if (isOpen) {
          document.body.style.overflow = 'hidden';
          window.__lenis?.stop();
      } else {
          document.body.style.overflow = 'unset';
          window.__lenis?.start();
      }

      // Handle horizontal scroll with mouse wheel
      const container = scrollContainerRef.current;
      const handleWheel = (e) => {
          if (container && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
              e.preventDefault();
              container.scrollBy({ left: e.deltaY * 3, behavior: 'smooth' });
          }
      };

      if (container) {
          container.addEventListener('wheel', handleWheel, { passive: false });
      }

      return () => {
          document.body.style.overflow = 'unset';
          window.__lenis?.start();
          if (container) {
              container.removeEventListener('wheel', handleWheel);
          }
      }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
        <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl bg-slate-50 rounded-3xl p-6 md:p-10 relative max-h-[90vh] overflow-y-auto no-scrollbar"
        >
          <button 
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-800 shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
          >
              <i className="fi fi-br-cross text-xs"></i>
          </button>
          
          <div className="text-center mb-10 mt-4">
             <div className="text-[10px] font-bold text-KITH-primary tracking-widest uppercase mb-2">
               {t('booking_flow.subtitle')}
             </div>
             <h2 className="text-2xl md:text-3xl font-bold text-gray-800 leading-tight mb-3">
               {t('booking_flow.title')}
             </h2>
             <p className="text-sm text-gray-500 max-w-2xl mx-auto">
               {t('booking_flow.desc')}
             </p>
          </div>

          <div ref={scrollContainerRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-2 pb-4 no-scrollbar scrollbarHide">
            {steps.map((step) => (
              <div 
                key={step.id} 
                className="w-[85vw] md:w-[320px] shrink-0 snap-center bg-white rounded-2xl p-6 border border-gray-100 shadow-sm flex flex-col gap-4 hover:shadow-md hover:border-KITH-primary/30 transition-all duration-300 group"
              >
                <div className="flex items-center justify-between">
                    <div className='w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center font-bold text-KITH-primary'>
                      {step.id}
                    </div>
                    <div className='w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-700 group-hover:bg-KITH-primary group-hover:text-white transition-colors'>
                      <i className={`${step.icon} text-lg mt-1`}></i>
                    </div>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-800 leading-tight mb-2">
                    {step.title}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}

export default BookingProcessFlow;
